// Matches widget (v2) — the prototype's per-match Gantt on live data. One row per
// match: a green bar for the minutes on the pitch, on a shared 0→axisMax minute
// axis, with toggleable incident dots (Shots / Goals / Key Passes / Assists).
// Single-player; a dropped player REPLACES the one shown (registry replaceOnDrop).
//
// Reuses the tested pure logic: buildTimeline (bars + axis, from appearances) and
// buildIncidents/matchLabel (matchIncidents.ts). Opponent + H/A come from a
// /matches join keyed on matchId (the appearances feed carries neither), against
// the player's own team from their bio — date fallback when it can't be resolved.
//
// Deferred overlays (need data we don't serve cleanly): Team Goals, Position
// Changes — see docs/frontend-v2/deferred.md.

import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { MatchSummary } from "@catbento/shared";
import type { WidgetProps } from "../registry.js";
import type { PlayerRef } from "../types.js";
import {
  usePlayerAppearances,
  usePlayerShots,
  usePlayerPasses,
  usePlayerCareerSeasons,
  usePlayerCompetitions,
  useMatchGoals,
} from "../../data/players.js";
import { useMatches } from "../../data/leagues.js";
import { CompetitionScopePicker } from "./CompetitionScopePicker.js";
import {
  resolveCompetitionScope,
  defaultCompetitionScope,
  type CompetitionScope,
} from "../competitionScope.js";
import { buildTimeline, axisMaxForValues } from "../../widgets/appearancesTimeline.js";
import {
  buildIncidents,
  incidentsByMatch,
  teamGoalsByMatch,
  buildPositionChanges,
  matchLabel,
  matchBarTooltip,
  type IncidentKind,
} from "../matchIncidents.js";
import { useCanvasStore } from "../store.js";
import { theme } from "../theme.js";

// Team Goals colour by side: the subject player's team vs the opponent. Distinct
// from the player-event overlay colours above so a "for" goal doesn't read as an
// assist etc. (Ben will perfect the exact hues later.)
const TEAM_GOAL_FOR = "#2ecc71";
const TEAM_GOAL_AGAINST = "#ff6b81";
const POSITION_CHANGE = "#00c2ff"; // electric blue, matching the prototype

const OVERLAYS: { kind: IncidentKind; label: string; color: string }[] = [
  { kind: "shot", label: "Shots", color: "#cccccc" },
  { kind: "goal", label: "Goals", color: "#f5a623" },
  { kind: "keyPass", label: "Key Passes", color: "#4d8bf5" },
  { kind: "assist", label: "Assists", color: "#b06df2" },
  { kind: "teamGoal", label: "Team Goals", color: TEAM_GOAL_FOR },
  { kind: "positionChange", label: "Position Changes", color: POSITION_CHANGE },
];

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export default function MatchesWidget({ instanceId, players, state, onPlayerDragStart }: WidgetProps) {
  const ref = players[0] ?? null;
  const playerId = ref?.playerId ?? null;
  const competitionId = ref?.competitionId ?? null;
  const seasonId = ref?.seasonId ?? null;

  // Overlays are OFF by default (matches the prototype). Local view state — a
  // display preference, not board data, so it doesn't touch save/undo.
  const [active, setActive] = useState<ReadonlySet<IncidentKind>>(new Set());
  function toggle(kind: IncidentKind) {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }

  // Custom hover tooltip — INSTANT (native `title` waits ~1s on the OS) and
  // multi-line, matching the prototype. `tip(text)` returns the handlers for any
  // bar/dot; the layer renders once via a portal so it's never clipped by the
  // widget's own overflow/scroll. Follows the cursor in viewport coords, so zoom
  // (a CSS transform on the canvas) doesn't offset it.
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const tip = (text: string) => ({
    onMouseEnter: (e: React.MouseEvent) => setTooltip({ text, x: e.clientX, y: e.clientY }),
    onMouseMove: (e: React.MouseEvent) =>
      setTooltip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t)),
    onMouseLeave: () => setTooltip(null),
  });

  // Season-first (C2d): career seasons drive the competition options, and a
  // competition SCOPE (All / one / except — Ben's pill) says which competitions'
  // matches to show. The scope lives in widget `state`; default = the ref's
  // single anchor competition (so it opens on one competition, like before). The
  // scope resolves to the competition_id[] every Matches feed is fetched across.
  const seasonsQ = usePlayerCareerSeasons(playerId);
  const compsQ = usePlayerCompetitions(playerId, seasonId);
  const seasonCompIds = compsQ.data?.competitions.map((c) => c.id) ?? [];
  const scope =
    (state.competitionScope as CompetitionScope | undefined) ??
    defaultCompetitionScope(competitionId, seasonCompIds);
  const resolvedComps = resolveCompetitionScope(scope, seasonCompIds);

  // LAZY overlay feeds: fetch a feed only when one of its overlays is toggled ON
  // (all overlays default OFF). Passing `null` as the competition disables the
  // query. This avoids a heavy shots/passes/goals read on every Matches open —
  // especially across a multi-competition scope ("All") — which was timing out
  // (25s query_timeout) and hammering the shared DB for data nobody had revealed.
  // React Query still caches, so the first toggle loads and later toggles are
  // instant. `appsQ` (the bars + position changes) is always needed.
  const wantShots = active.has("shot") || active.has("goal"); // both derive from shots
  const wantPasses = active.has("keyPass") || active.has("assist"); // both derive from passes
  const wantTeamGoals = active.has("teamGoal");

  const appsQ = usePlayerAppearances(playerId, resolvedComps, seasonId);
  const shotsQ = usePlayerShots(playerId, wantShots ? resolvedComps : null, seasonId);
  const passesQ = usePlayerPasses(playerId, wantPasses ? resolvedComps : null, seasonId);
  const matchesQ = useMatches(resolvedComps, seasonId);
  const goalsQ = useMatchGoals(playerId, wantTeamGoals ? resolvedComps : null, seasonId);

  const appearances = appsQ.data?.appearances ?? [];
  // Real-minute mode (clampToPeriod: false) so stoppage-time bars/dots aren't
  // collapsed onto 90 — the axis is sized to fit below.
  const timeline = useMemo(
    () => buildTimeline(appearances, { clampToPeriod: false }),
    [appsQ.data]
  );
  const incidents = useMemo(
    () => buildIncidents(shotsQ.data?.shots ?? [], passesQ.data?.passes ?? []),
    [shotsQ.data, passesQ.data]
  );
  const byMatch = useMemo(() => incidentsByMatch(incidents, active), [incidents, active]);
  const teamGoals = useMemo(
    () => teamGoalsByMatch(goalsQ.data?.goals ?? []),
    [goalsQ.data]
  );
  const positionChanges = useMemo(
    () => buildPositionChanges(appearances),
    [appsQ.data]
  );

  // Right edge of the minute axis: fit the latest minute we plot — the bars plus
  // whatever overlay data is loaded — with a 90 floor, rounded up to the next 10.
  // Real stoppage/extra time extends it instead of being clamped to 90. (With the
  // lazy feeds, an overlay's markers only exist once it's toggled on; the bars
  // already reach each match's last on-pitch minute, so the axis is stable for
  // the common case and only re-fits if a revealed marker sits beyond the bars.)
  const axisMax = useMemo(() => {
    const mins: number[] = [];
    for (const r of timeline.rows) mins.push(r.off);
    for (const inc of incidents) mins.push(inc.minute);
    for (const list of teamGoals.values()) for (const g of list) mins.push(g.minute);
    for (const list of positionChanges.values()) for (const m of list) mins.push(m.minute);
    return axisMaxForValues(mins);
  }, [timeline, incidents, teamGoals, positionChanges]);

  // The player's team in each match (for the opponent label) — from the
  // appearance, not the bio's current team (wrong for past seasons after a move).
  const teamIdByMatch = useMemo(() => {
    const map = new Map<number, number | null>();
    for (const a of appearances) map.set(a.matchId, a.teamId);
    return map;
  }, [appsQ.data]);
  const matchById = useMemo(() => {
    const map = new Map<number, MatchSummary>();
    for (const m of matchesQ.data?.matches ?? []) map.set(m.matchId, m);
    return map;
  }, [matchesQ.data]);

  function setSeason(newSeasonId: number) {
    if (!ref) return;
    // Changing season resets the competition scope — the old pick may not exist in
    // the new season, so re-derive the default (the anchor comp, else All).
    useCanvasStore.getState().updateWidget(instanceId, {
      players: [{ ...ref, seasonId: newSeasonId }],
      state: { ...state, competitionScope: undefined },
    });
  }

  function setScope(next: CompetitionScope) {
    useCanvasStore.getState().updateWidget(instanceId, {
      state: { ...state, competitionScope: next },
    });
  }

  if (!ref) return <div style={emptyStyle}>Search a player to begin.</div>;

  const seasons = seasonsQ.data?.seasons ?? [];
  // The header (player + season + competition scope) shows in EVERY state, so the
  // user can always change season / scope — even when the current scope has no
  // matches (e.g. the only competition excluded). `shell` wraps a body under it.
  const headerRow = (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
      <span
        onPointerDown={onPlayerDragStart ? (e) => onPlayerDragStart(e, ref as PlayerRef) : undefined}
        title="Drag onto another card to compare"
        style={{
          fontFamily: theme.fontBody, fontSize: 14, fontWeight: 600, color: theme.ink,
          cursor: onPlayerDragStart ? "grab" : "default", userSelect: "none", touchAction: "none",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}
      >
        {ref.displayName}
      </span>
      <span style={{ flex: 1 }} />
      <select value={seasonId ?? ""} onChange={(e) => setSeason(Number(e.target.value))} style={selectStyle}>
        {seasons.length === 0 && seasonId !== null ? <option value={seasonId}>current</option> : null}
        {seasons.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
      {/* Competition scope (All / one / except) — which competitions' matches show. */}
      <div style={{ width: 150 }}>
        <CompetitionScopePicker
          competitions={compsQ.data?.competitions ?? []}
          scope={scope}
          onChange={setScope}
        />
      </div>
    </div>
  );
  const shell = (body: React.ReactNode) => (
    <div style={{ fontFamily: theme.fontBody, color: theme.ink, fontSize: 12, display: "flex", flexDirection: "column", height: "100%" }}>
      {headerRow}
      {body}
    </div>
  );
  const bodyMsg = (text: string, danger = false) => (
    <div style={{ flex: 1, display: "grid", placeItems: "center", color: danger ? theme.danger : theme.inkMuted, fontSize: 13, textAlign: "center", padding: 12 }}>
      {text}
    </div>
  );

  if (appsQ.isLoading) return shell(bodyMsg("Loading matches…"));
  if (appsQ.isError) return shell(bodyMsg("Couldn’t load matches.", true));
  if (timeline.rows.length === 0) return shell(bodyMsg("No matches for this scope."));

  const ticks: number[] = [];
  for (let m = 0; m <= axisMax; m += 10) ticks.push(m);
  const pct = (minute: number) => `${(minute / axisMax) * 100}%`;

  return shell(
    <>
      {/* Overlay toggle pills */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {OVERLAYS.map((o) => {
          const on = active.has(o.kind);
          return (
            <button
              key={o.kind}
              onClick={() => toggle(o.kind)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px",
                background: theme.surface, borderRadius: 999, fontSize: 12, cursor: "pointer",
                color: on ? theme.ink : theme.inkFaint,
                border: `1px solid ${on ? theme.accent : theme.border}`,
              }}
            >
              <span
                style={{
                  width: 8, height: 8, borderRadius: 999, flexShrink: 0,
                  // Team Goals is a two-sided overlay (for vs against) → a split
                  // swatch hints both colours; the rest are a single dot.
                  background: o.kind === "teamGoal"
                    ? `linear-gradient(90deg, ${TEAM_GOAL_FOR} 50%, ${TEAM_GOAL_AGAINST} 50%)`
                    : o.color,
                }}
              />
              {o.label}
            </button>
          );
        })}
      </div>

      {/* Minute axis header */}
      <div style={{ display: "flex", alignItems: "flex-end", marginBottom: 2 }}>
        <div style={{ width: LABEL_W, flexShrink: 0 }} />
        <div style={{ position: "relative", flex: 1, height: 14 }}>
          {ticks.map((t) => (
            <span key={t} style={{ position: "absolute", left: pct(t), transform: "translateX(-50%)", color: theme.inkFaint, fontSize: 9 }}>
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Rows — scrolls when a full season is taller than the widget */}
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {timeline.rows.map((row, i) => {
          const label = matchLabel(
            matchById.get(row.matchId),
            teamIdByMatch.get(row.matchId) ?? null,
            shortDate(row.date)
          );
          const dots = byMatch.get(row.matchId) ?? [];
          return (
            <div key={`${row.matchId}-${i}`} style={{ display: "flex", alignItems: "center", height: ROW_H }}>
              <div style={{ width: LABEL_W, flexShrink: 0, display: "flex", gap: 4, alignItems: "baseline", overflow: "hidden" }}>
                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: theme.inkMuted }} title={label.text}>
                  {label.text}
                </span>
                {label.homeAway ? <span style={{ color: theme.inkFaint, fontSize: 10, flexShrink: 0 }}>{label.homeAway}</span> : null}
              </div>
              {/* Track: gridlines + bar + dots */}
              <div
                style={{
                  position: "relative", flex: 1, height: ROW_H,
                  backgroundImage: `repeating-linear-gradient(90deg, ${theme.border}55 0, ${theme.border}55 1px, transparent 1px, transparent ${100 / (axisMax / 10)}%)`,
                }}
              >
                {row.played ? (
                  <div
                    style={{
                      position: "absolute", top: "50%", transform: "translateY(-50%)",
                      left: pct(row.on), width: `calc(${pct(row.off - row.on)} + 1px)`,
                      height: 9, background: theme.accent, opacity: 0.85, borderRadius: 3,
                    }}
                    {...tip(matchBarTooltip(row, label, matchById.get(row.matchId)))}
                  />
                ) : (
                  <div
                    style={{ position: "absolute", top: "50%", left: 0, transform: "translate(-50%,-50%)", width: 5, height: 5, borderRadius: 999, border: `1px solid ${theme.inkFaint}` }}
                    {...tip(matchBarTooltip(row, label, matchById.get(row.matchId)))}
                  />
                )}
                {dots.map((inc, j) => {
                  const color = OVERLAYS.find((o) => o.kind === inc.kind)?.color ?? theme.ink;
                  return (
                    <div
                      key={j}
                      style={{
                        position: "absolute", top: "50%", left: pct(inc.minute), transform: "translate(-50%,-50%)",
                        width: 9, height: 9, borderRadius: 999, background: color,
                        border: `1.5px solid ${theme.tile}`, boxSizing: "content-box",
                      }}
                      {...tip(inc.tooltip)}
                    />
                  );
                })}
                {/* Team Goals: a dot per goal in the match, coloured by side
                    (player's team vs opponent); tooltip names the scorer + team. */}
                {active.has("teamGoal")
                  ? (teamGoals.get(row.matchId) ?? []).map((g, j) => (
                      <div
                        key={`tg-${j}`}
                        style={{
                          position: "absolute", top: "50%", left: pct(g.minute), transform: "translate(-50%,-50%)",
                          width: 9, height: 9, borderRadius: 999,
                          background: g.isPlayerTeam ? TEAM_GOAL_FOR : TEAM_GOAL_AGAINST,
                          border: `1.5px solid ${theme.tile}`, boxSizing: "content-box",
                        }}
                        {...tip(g.tooltip)}
                      />
                    ))
                  : null}
                {/* Position Changes: a thin vertical blue tick at the switch
                    minute (a "moved position" marker, not a round event dot). */}
                {active.has("positionChange")
                  ? (positionChanges.get(row.matchId) ?? []).map((c, j) => (
                      <div
                        key={`pc-${j}`}
                        style={{
                          position: "absolute", top: "50%", left: pct(c.minute), transform: "translate(-50%,-50%)",
                          width: 3, height: 14, borderRadius: 1,
                          background: POSITION_CHANGE, border: `1px solid ${theme.tile}`, boxSizing: "content-box",
                        }}
                        {...tip(c.tooltip)}
                      />
                    ))
                  : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Instant hover tooltip — portalled to body so it's never clipped, fixed
          to the cursor. pre-line keeps the multi-line bar/shot text readable. */}
      {tooltip
        ? createPortal(
            <div
              style={{
                position: "fixed", left: tooltip.x + 12, top: tooltip.y + 12, zIndex: 9999,
                pointerEvents: "none", background: theme.tile, color: theme.ink,
                border: `1px solid ${theme.border}`, borderRadius: 6, padding: "5px 8px",
                fontSize: 11, lineHeight: 1.35, whiteSpace: "pre-line", maxWidth: 240,
                boxShadow: theme.shadowMd, fontFamily: theme.fontBody,
              }}
            >
              {tooltip.text}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

const LABEL_W = 118;
const ROW_H = 18;

const selectStyle: React.CSSProperties = {
  background: theme.field, color: theme.inkMuted, border: `1px solid ${theme.border}`,
  borderRadius: 4, fontSize: 11, padding: "2px 4px",
};

const emptyStyle: React.CSSProperties = {
  display: "grid", placeItems: "center", height: "100%",
  color: theme.inkMuted, fontSize: 13, textAlign: "center", padding: 12,
};
